import type { SignatureRecipient, SignatureRecipientAudit } from '@/types';

/**
 * Tekenbevoegden aan klantzijde van een SIGNATURE_PAGE-blok.
 *
 * De array `content.recipients` is leidend. De oudere losse velden
 * (`recipientName`, `recipientSignature`, ...) blijven meegeschreven als samenvatting:
 * naam/e-mail van de eerste tekenbevoegde en `recipientSignedAt` pas zodra *alle*
 * tekenbevoegden hebben getekend. Spiegelbeeld van
 * `packages/backend/src/utils/signatureRecipients.ts`.
 */

export const MAX_SIGNATURE_RECIPIENTS = 3;

type ContentRecord = Record<string, unknown>;

function isRecord(value: unknown): value is ContentRecord {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function asTrimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

export function buildRecipientId(index: number, contactId?: string | null): string {
  const trimmed = asTrimmedString(contactId);
  return trimmed ? `hs-${trimmed}` : `recipient-${index + 1}`;
}

function normalizeRecipient(entry: ContentRecord, index: number): SignatureRecipient {
  return {
    id: asTrimmedString(entry.id) || buildRecipientId(index, asTrimmedString(entry.contactId)),
    name: asTrimmedString(entry.name),
    email: asTrimmedString(entry.email),
    signature: asTrimmedString(entry.signature) || null,
    signedAt: asTrimmedString(entry.signedAt) || null,
    audit: isRecord(entry.audit) ? (entry.audit as SignatureRecipientAudit) : null,
  };
}

export function readSignatureRecipients(content: unknown): SignatureRecipient[] {
  const record = isRecord(content) ? content : {};
  const raw = record.recipients;

  if (Array.isArray(raw)) {
    const list = raw.filter(isRecord).map(normalizeRecipient);
    if (list.length > 0) return list.slice(0, MAX_SIGNATURE_RECIPIENTS);
  }

  const name = asTrimmedString(record.recipientName);
  const email = asTrimmedString(record.recipientEmail);
  const signature = asTrimmedString(record.recipientSignature);
  const signedAt = asTrimmedString(record.recipientSignedAt);
  if (!name && !email && !signature) return [];

  return [
    {
      id: buildRecipientId(0),
      name,
      email,
      signature: signature || null,
      signedAt: signedAt || null,
      audit: isRecord(record.signatureAudit) ? (record.signatureAudit as SignatureRecipientAudit) : null,
    },
  ];
}

/** Zelfde als `readSignatureRecipients`, maar garandeert minstens één tekenvak. */
export function readSignatureRecipientsForSigning(content: unknown): SignatureRecipient[] {
  const list = readSignatureRecipients(content);
  return list.length > 0 ? list : [{ id: buildRecipientId(0), name: '', email: '', signature: null, signedAt: null }];
}

export function isRecipientSigned(recipient: SignatureRecipient): boolean {
  return Boolean(recipient.signature && recipient.signature.trim());
}

export function countSignedRecipients(recipients: SignatureRecipient[]): number {
  return recipients.filter(isRecipientSigned).length;
}

export function allRecipientsSigned(recipients: SignatureRecipient[]): boolean {
  return recipients.length > 0 && recipients.every(isRecipientSigned);
}

export function lastRecipientSignedAt(recipients: SignatureRecipient[]): string | null {
  const timestamps = recipients
    .map((recipient) => recipient.signedAt)
    .filter((value): value is string => Boolean(value));
  if (timestamps.length === 0) return null;
  return timestamps.reduce((latest, value) => (value > latest ? value : latest));
}

function serializeRecipient(recipient: SignatureRecipient): ContentRecord {
  const serialized: ContentRecord = {
    id: recipient.id,
    name: recipient.name,
    email: recipient.email,
  };
  if (recipient.signature) serialized.signature = recipient.signature;
  if (recipient.signedAt) serialized.signedAt = recipient.signedAt;
  if (recipient.audit) serialized.audit = recipient.audit;
  return serialized;
}

/** Schrijf de tekenbevoegden terug en houd de legacy-samenvattingsvelden in sync. */
export function withSignatureRecipients(content: unknown, recipients: SignatureRecipient[]): ContentRecord {
  const record = isRecord(content) ? { ...content } : {};
  const capped = recipients.slice(0, MAX_SIGNATURE_RECIPIENTS);
  const primary = capped[0];

  record.recipients = capped.map(serializeRecipient);
  record.recipientName = primary?.name ?? '';
  record.recipientEmail = primary?.email ?? '';

  if (primary?.signature) {
    record.recipientSignature = primary.signature;
  } else {
    delete record.recipientSignature;
  }

  if (allRecipientsSigned(capped)) {
    record.recipientSignedAt = lastRecipientSignedAt(capped) ?? new Date().toISOString();
  } else {
    delete record.recipientSignedAt;
  }

  if (primary?.audit) {
    record.signatureAudit = primary.audit;
  } else {
    delete record.signatureAudit;
  }

  return record;
}
